// Composant Carte Info Candidat Réutilisable
import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';

interface CandidateInfoCardProps {
  name: string;
  additionalInfo?: string;
  coLocataire?: string;
  property?: string;
  score: number;
  maxScore?: number;
  scoreLabel?: string;
  showProgressBar?: boolean;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.xl};
  background: white;
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.sm};

  @media print {
    box-shadow: none;
    page-break-inside: avoid;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${theme.spacing.lg};
  }
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  flex-shrink: 0;
`;

const Details = styled.div``;

const Name = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  margin: 0 0 ${theme.spacing.xs} 0;
  color: ${theme.colors.text};
`;

const Meta = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;

  & + & {
    margin-top: ${theme.spacing.xs};
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing.sm};
`;

const Badge = styled.div<{ score: number; maxScore: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  background: ${props => {
    const percentage = (props.score / props.maxScore) * 100;
    if (percentage >= 80) return theme.colors.success + '20';
    if (percentage >= 60) return theme.colors.info + '20';
    if (percentage >= 40) return theme.colors.warning + '20';
    return theme.colors.error + '20';
  }};
  color: ${props => {
    const percentage = (props.score / props.maxScore) * 100;
    if (percentage >= 80) return theme.colors.success;
    if (percentage >= 60) return theme.colors.info;
    if (percentage >= 40) return theme.colors.warning;
    return theme.colors.error;
  }};
  border: 2px solid currentColor;
`;

const ScoreLabel = styled.span`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  text-transform: lowercase;
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  position: relative;
  padding-top: ${theme.spacing.md};

  @media print {
    width: 250px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    width: 100%;
  }
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 12px;
  background: linear-gradient(
    to right,
    ${theme.colors.error} 0%,
    ${theme.colors.error} 20%,
    ${theme.colors.warning} 20%,
    ${theme.colors.warning} 40%,
    #FFC107 40%,
    #FFC107 60%,
    ${theme.colors.success} 60%,
    ${theme.colors.success} 80%,
    ${theme.colors.info} 80%,
    ${theme.colors.info} 100%
  );
  border-radius: ${theme.borderRadius.full};
  position: relative;

  @media print {
    display: none;
  }
`;

const ProgressBarLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const ProgressBarIndicator = styled.div<{ position: number }>`
  position: absolute;
  top: 50%;
  left: ${props => props.position}%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  background: ${theme.colors.text};
  border: 3px solid white;
  border-radius: ${theme.borderRadius.full};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  @media print {
    display: none;
  }
`;

export const CandidateInfoCard: React.FC<CandidateInfoCardProps> = React.memo(({
  name,
  additionalInfo,
  coLocataire,
  property,
  score,
  maxScore = 500,
  scoreLabel = 'bon',
  showProgressBar = true
}) => {
  const initials = name.split(' ').map(n => n[0]).join('');
  const percentage = (score / maxScore) * 100;

  return (
    <Container>
      <Info>
        <Avatar>{initials}</Avatar>
        <Details>
          <Name>{name}{additionalInfo && `, ${additionalInfo}`}</Name>
          {coLocataire && <Meta>Co-locataire : {coLocataire}</Meta>}
          {property && (
            <Meta>
              Candidat pour la propriété : <strong>{property}</strong>
            </Meta>
          )}
        </Details>
      </Info>
      <BadgeContainer>
        <Badge score={score} maxScore={maxScore}>
          {score} <ScoreLabel>{scoreLabel}</ScoreLabel>
        </Badge>
        {showProgressBar && (
          <ProgressBarContainer>
            <ProgressBarTrack>
              <ProgressBarIndicator position={percentage} />
            </ProgressBarTrack>
            <ProgressBarLabels>
              <span>100</span>
              <span>200</span>
              <span>300</span>
              <span>400</span>
              <span>500</span>
            </ProgressBarLabels>
          </ProgressBarContainer>
        )}
      </BadgeContainer>
    </Container>
  );
});
